import closeIcon from '@/assets/toast/close.svg';
import errorIcon from '@/assets/toast/error.svg';
import { SuccessToastMark } from '@/shared/components/SuccessToastMark';
import { messages } from '@/shared/i18n';
import { cn } from '@/shared/lib/cn';
import { useToastStore } from '@/store';
import type { ToastVariant } from '@/store';

const SUCCESS_DEFAULT_TITLE = messages.common.toastSuccessTitle;

const ERROR_THEME = {
  defaultTitle: messages.common.toastErrorTitle,
  ringOuterClass:
    'flex size-[76px] shrink-0 items-center justify-center rounded-full bg-danger/5 shadow-halo-danger',
  ringMiddleClass:
    'flex size-[60px] shrink-0 items-center justify-center rounded-full border-[2px] border-solid border-danger/15 bg-danger-bg/50',
  iconSrc: errorIcon,
  iconSizeClass: 'size-[42px]',
} as const;

const DEFAULT_TITLE: Record<ToastVariant, string> = {
  success: SUCCESS_DEFAULT_TITLE,
  error: ERROR_THEME.defaultTitle,
};

interface ToastCloseButtonProps {
  onClick: () => void;
}

function ToastCloseButton({ onClick }: ToastCloseButtonProps) {
  return (
    <button
      type="button"
      aria-label={messages.common.dismissToast}
      onClick={onClick}
      className="shrink-0 self-start rounded-md p-1 opacity-60 transition-opacity hover:opacity-100"
    >
      <img src={closeIcon} alt="" className="size-3" />
    </button>
  );
}

export function ToastHost() {
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);

  if (toasts.length === 0) {
    return null;
  }

  return (
    <div
      className="pointer-events-none fixed left-1/2 top-5 z-[100] flex max-w-[calc(100vw-2rem)] -translate-x-1/2 flex-col items-center gap-2"
      aria-live="polite"
      aria-relevant="additions text"
    >
      {toasts.map((t) => {
        const title = t.title ?? DEFAULT_TITLE[t.variant];
        const role = t.variant === 'error' ? 'alert' : 'status';
        return (
          <div
            key={t.id}
            role={role}
            className={cn(
              'pointer-events-auto flex w-auto min-w-[280px] items-center gap-4 rounded-2xl bg-white p-4 shadow-toast',
              t.layout === 'wide'
                ? 'max-w-[min(680px,calc(100vw-2rem))]'
                : 'max-w-[min(460px,calc(100vw-2rem))]',
            )}
          >
            {t.variant === 'success' ? (
              <SuccessToastMark />
            ) : (
              <div className={ERROR_THEME.ringOuterClass} aria-hidden>
                <div className={ERROR_THEME.ringMiddleClass}>
                  <img src={ERROR_THEME.iconSrc} alt="" className={ERROR_THEME.iconSizeClass} />
                </div>
              </div>
            )}
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <p
                className={cn(
                  'font-semibold leading-normal text-ink',
                  t.layout === 'wide' ? 'text-xl' : 'text-[17px] font-bold leading-tight',
                )}
              >
                {title}
              </p>
              {t.messageNode ?? (
                t.message ? (
                  <p
                    className={cn(
                      'text-sm leading-normal',
                      t.variant === 'success'
                        ? t.layout === 'wide'
                          ? 'font-normal text-ink'
                          : 'font-normal text-ink-muted'
                        : 'font-medium text-ink',
                    )}
                  >
                    {t.message}
                  </p>
                ) : null
              )}
            </div>
            <ToastCloseButton
              onClick={() => {
                dismiss(t.id);
              }}
            />
          </div>
        );
      })}
    </div>
  );
}
